import { LOAD_PIXEL_SERVER } from 'constants/reduxActions';
import { postMartyPixel } from 'apis/martyPixel';
import { getPageLang, getPageTitle } from 'helpers/ClientUtils';
import { fetchMartyPixelErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import logger from 'middleware/logger';
/**
 * Fires a Google Tag Manager event and dispatches a Redux action with
 * tracking data.
 *
 * @param {String} pageType
 * the "pageType" key from the pixel bucket
 *
 * @param {Object} [trackingPayload={}]
 * additional data to post to the server for this pageType
 *
 * @param {String} [pageId='']
 * an additional identifier when combined with pageType to use when determining
 * when to fire the pixel server message. For instance to identify client
 * routing between different product pages. (not sent to pixel server)
 */
export function firePixelServer(pageType, trackingPayload = {}, pageId = '', pixelEventData = null) {
  return function (dispatch, getState) {
    const action = makePixelServerAction(getState(), pageType, trackingPayload, pageId, pixelEventData);
    dispatch(action);
  };
}

/**
 * @internal
 *
 * @param pageType
 * @param trackingPayload
 * @param sessionId
 * @return {Promise<unknown>} promise with event pushed, or null if event not pushed
 */
export const fireGTMEvent = (pageType, trackingPayload, sessionId = '') => {
  let finalTrackingPayload;
  if (pageType === 'confirmation') {
    const event = { pageType, trackingPayload };
    finalTrackingPayload = modifyOrderConfirmationEvent(event).trackingPayload;
  } else {
    finalTrackingPayload = trackingPayload;
  }
  const event = {
    event: pageType,
    sessionId: sessionId,
    ...finalTrackingPayload
  };

  // TODO what should be happening if this executes server side? Should we be queueing events up to fire client side like we do for amethyst?
  return new Promise(resolve => {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const callback = () => {
        window.dataLayer.push(event);
        resolve(event);
      };
      if (window.requestIdleCallback) {
        // if we have request idle callback, do the call then
        window.requestIdleCallback(callback);
        return;
      } else {
        // if we do not have request idle callback, do the call in set timeout to defer it a bit.
        setTimeout(callback, 5);
        return;
      }
    }
    resolve(null);
  });
};

export function makePixelServerAction(state, pageType, trackingPayload = {}, pageId = '', pixelEventData = null) {
  const {
    router: {
      location: { search }
    },
    cookies
  } = state;
  // data.pageId field is used to "cache bust" for pixel pages with no
  // parameters and the same pageType (e.g landing pages).
  const pageLang = getPageLang();
  const pageTitle = getPageTitle();
  const sessionId = cookies['session-id'];

  // if pixelEventData is passed use it rather than the tracking payload as amethyst / pixel event data may not conform to the same shape
  const pixelData = pixelEventData ? pixelEventData : trackingPayload;

  // Sending pixel events to GTM
  fireGTMEvent(pageType, pixelData, sessionId);

  return {
    type: LOAD_PIXEL_SERVER,
    pageType,
    data: {
      customerCountryCode: extractCustomerCountryCode(cookies),
      pageId,
      pageLang,
      pageTitle,
      sessionId,
      ...pixelData
    },
    queryString: search
  };
}

export function extractCustomerCountryCode(cookies) {
  return cookies.geo?.split('/')[0] || null;
}

// TODO This isn't a Marketing pixel and probably should be in a different module entirely.
export function fireMartyPixel(qs, type, endpoints = { postMartyPixel }) {
  return function () {
    return endpoints
      .postMartyPixel(`type=${type}&${qs}`)
      .then(fetchMartyPixelErrorMiddleware)
      .catch(() => {
        logger('Unable to get valid response from /martypixel endpoint, not firing marty pixel server.');
      });
  };
}

// Addresses get their zip codes truncated due to PII/Data classification policies
export function modifyOrderConfirmationEvent(orderEvent) {
  const updatedOrders = [];
  orderEvent.trackingPayload?.orders.forEach(order => {
    const { billingAddress, shippingAddress } = order;
    const orderObj = {
      ...order,
      billingAddress: {
        ...billingAddress,
        postalCode: billingAddress?.postalCode.slice(0, 5)
      },
      shippingAddress: {
        ...shippingAddress,
        postalCode: shippingAddress?.postalCode.slice(0, 5)
      }
    };
    updatedOrders.push(orderObj);
  });
  const { billingAddress, shippingAddress } = orderEvent?.trackingPayload?.order;
  const event = {
    ...orderEvent,
    trackingPayload: {
      order: {
        ...orderEvent.trackingPayload.order,
        billingAddress: {
          ...billingAddress,
          postalCode: billingAddress?.postalCode.slice(0, 5)
        },
        shippingAddress: {
          ...shippingAddress,
          postalCode: shippingAddress?.postalCode.slice(0, 5)
        }
      },
      orders: updatedOrders
    }
  };
  return event;
}
